/**
 * Optimistic delete for a chat thread (PROD-452). The DELETE can take minutes in production (pi-server waits on
 * an S3 artifact-prefix cleanup before it answers), so callers must not await it: the row leaves the shared
 * threads cache in onMutate, and a failure splices it back and notifies.
 */

import { DefaultError, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNotifications } from "hooks/notifications"
import { Creators } from "model/apiWrapper"
import { useMemo } from "react"
import { useDispatch } from "react-redux"
import { FetchThreadsResponse, Thread } from "../types/thread"
import { getThreadsQueryKey, removeThreadFromCache, restoreThreadToCache } from "../utils/threads-cache"

interface DeleteThreadPayload {
  accountAlias: string
  threadId: string
}

interface DeleteThreadContext {
  /** Only the deleted row and its slot — a full-list snapshot would rewind mutations made during the long DELETE. */
  deletedThread: Thread | undefined
  deletedIndex: number
}

export function useDeleteThread() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()

  const { mutate } = useMutation<unknown, DefaultError, DeleteThreadPayload, DeleteThreadContext>({
    mutationFn: ({ accountAlias, threadId }) => {
      return new Promise((resolve, reject) => {
        dispatch(Creators.delete(`/api/${accountAlias}/ai/chat/threads/${threadId}`, undefined, resolve, reject))
      })
    },
    onMutate: async ({ accountAlias, threadId }) => {
      const key = getThreadsQueryKey(accountAlias)
      // Stop an in-flight list GET from re-adding the row, then snapshot just this row and its position.
      await queryClient.cancelQueries({ queryKey: key })
      const cached = queryClient.getQueryData<FetchThreadsResponse>(key)
      const deletedIndex = cached?.threads?.findIndex((thread) => thread.threadId === threadId) ?? -1
      const deletedThread = deletedIndex >= 0 ? cached?.threads[deletedIndex] : undefined
      removeThreadFromCache(queryClient, key, threadId)
      return { deletedThread, deletedIndex }
    },
    // Restore the row, then refetch: the failure may still have deleted server-side (pi-server drops its row
    // before the artifact cleanup that can fail), so the restore is a guess and only the list GET is truth.
    onError: (_error, { accountAlias }, context) => {
      const key = getThreadsQueryKey(accountAlias)
      // Only claim a restore when there was a row to splice back — a delete against an already-absent row rolls nothing back.
      if (context?.deletedThread !== undefined) {
        restoreThreadToCache(queryClient, key, context.deletedThread, context.deletedIndex)
        notifs.error("The conversation couldn't be deleted and has been restored. Please try again.")
      } else {
        notifs.error("The conversation couldn't be deleted. Please try again.")
      }
      queryClient.invalidateQueries({ queryKey: key })
    },
  })

  // Fire-and-forget by design: `mutate` never throws, so no caller can gate its UI on the response.
  return useMemo(() => ({ deleteThread: mutate }), [mutate])
}
